// Swedish bank account utilities built on the `kontonummer` registry: bank lookup and
// transport normalization (ADA-4344, moved out of validation.ts in the PR #912 review —
// these derive values, they don't validate). The validation predicates in validation.ts
// build on these; imports flow validation → here, never the other way.

import Kontonummer from 'kontonummer'

/**
 * The bank a clearing number belongs to, or undefined when no bank claims it.
 *
 * This is what replaces the design's "Välj bank" dropdown: in Sweden the bank follows from the
 * clearing number, so asking for both invites a contradiction. The name is shown back to the
 * debtor as confirmation that they typed the number they meant.
 */
export function bankNameForClearing(clearingNumber: string): string | undefined {
  const clearing = clearingNumber.replace(/\D/g, '')
  // Shape gate mirrors validation.ts's isValidClearingNumber (kept inline so the import
  // direction stays one-way).
  if (!/^[0-9]{4,5}$/.test(clearing)) return undefined
  if (clearing.length === 5 && (!clearing.startsWith('8') || !passesLuhn(clearing))) {
    // The package reads only the first four digits, so "12345" would render as Danske Bank
    // while the alert calls the clearing number invalid — no name for a rejected clearing.
    return undefined
  }
  return bankInfoFor(clearing)?.bankName
}

/** The registry entry claiming a clearing number, or undefined when no bank does. Verified:
 * the package THROWS for a clearing number outside every known range (9999), it does not
 * return undefined — so the catch is the "no such bank" path, not a safety net. */
function bankInfoFor(clearingNumber: string): ReturnType<typeof Kontonummer.getSortingCodeInfo> | undefined {
  try {
    return Kontonummer.getSortingCodeInfo(Number(clearingNumber))
  } catch {
    return undefined
  }
}

/**
 * Clearing + account number reduced to what is actually sent and stored (ADA-4344 decision #5):
 * digits only, leading zeros beyond the bank's maximum account length dropped, and type 1
 * accounts zero-padded to the canonical seven digits — the same normalization the api applies,
 * so the value the debtor confirmed is the value that is paid.
 * An unknown bank normalizes to digits only; the registry check then rejects it separately.
 */
export function normalizeBankAccount(
  clearingNumber: string,
  accountNumber: string
): { clearingNumber: string; accountNumber: string } {
  const clearing = clearingNumber.replace(/\D/g, '')
  let account = accountNumber.replace(/\D/g, '')
  const bank = bankInfoFor(clearing)
  if (bank == null || account === '') return { clearingNumber: clearing, accountNumber: account }
  // Every type 2 entry carries accountMaxLength (verified against the registry); the
  // fallback only satisfies the type and means "strip nothing".
  const maxLength = bank.type === 1 ? 7 : (bank.accountMaxLength ?? account.length)
  while (account.length > maxLength && account.startsWith('0')) account = account.slice(1)
  // Type 1 accounts are stored padded to their full seven positions (the specification's
  // canonical form): unpadded they would violate the api schema's ^[0-9]{5,15}$ and
  // concatenate into a payment-file form the bank rejects.
  if (bank.type === 1) account = account.padStart(7, '0')
  return { clearingNumber: clearing, accountNumber: account }
}

/**
 * A giro number reduced to what is actually sent and stored (ADA-4350 decision #5): digits only,
 * leading zeros dropped.
 *
 * Luhn is invariant under left zero-padding and the transport format (BGMAX) re-pads to its own
 * width on the way out, so the significant digits ARE the number. Stripping them gives one
 * canonical stored form, so the same giro cannot end up stored in two spellings — the api applies
 * exactly this normalization (SwedishGiroHelper), and the length is judged on the result.
 *
 * Both instruments (plusgiro joined in ADA-4349). Note that the strip can move a number across a
 * length boundary — '007' is a one-digit plusgiro, below the 2–8 floor — so every caller must
 * length-check the RESULT, not the raw input.
 */
export function normalizeGiro(value: string): string {
  return value.replace(/\D/g, '').replace(/^0+/, '')
}

/**
 * Luhn for a giro number — the mod 10 check plus the guard the bare sum cannot express.
 *
 * `passesLuhn` sums digits, so '' and '0000000' both reduce to `0 % 10 === 0` and would read as
 * valid; the giro shape regexes accept the all-zero form, so nothing else stops it. The api
 * guards the same trap on its side (ADA-4350 decision #2), so both ends reject it alike.
 */
export function passesGiroLuhn(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  if (!/[1-9]/.test(digits)) return false
  return passesLuhn(digits)
}

/**
 * Luhn (mod 10) check, the algorithm behind bankgiro/plusgiro check digits and the fifth digit
 * of Swedbank's five-digit clearing numbers.
 *
 * Hand-rolled because `kontonummer` covers bank accounts only, and this is the whole of the rule.
 * Callers on giro numbers must go through `passesGiroLuhn` — see its note on all-zero values.
 */
export function passesLuhn(value: string): boolean {
  let sum = 0
  let double = false
  for (let index = value.length - 1; index >= 0; index -= 1) {
    let digit = Number(value[index])
    if (double) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    double = !double
  }
  return sum % 10 === 0
}
